import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, ArrowLeft, Check, X, DollarSign, Shield, Briefcase,
  Search, RefreshCw, Clock, UserCheck, UserX, Wallet, Trash2, Lock, Mail, Key, Copy, Eye, EyeOff, MapPin, KeyRound, LogOut, Smartphone, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available wilayah codes
const WILAYAH_CODES = ["02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16"];

function getToken() {
  return localStorage.getItem("admin_token") || "";
}
function authHeader() {
  return { Authorization: `Bearer ${getToken()}` };
}

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

// Format number with thousand separator (dots)
const formatNumberWithDots = (value) => {
  if (!value) return "";
  // Remove all non-digits
  const cleanValue = value.toString().replace(/\D/g, "");
  // Add thousand separators
  return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Parse formatted number back to raw number
const parseFormattedNumber = (value) => {
  if (!value) return "";
  return value.toString().replace(/\./g, "");
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [viewMode, setViewMode] = useState("users"); // "users" or "customers"
  const [isLoading, setIsLoading] = useState(true);
  const [topupModal, setTopupModal] = useState(null);
  const [topupAmount, setTopupAmount] = useState(""); // Displayed with dots
  const [topupAmountRaw, setTopupAmountRaw] = useState(""); // Raw number for API
  const [topupNote, setTopupNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [resetCodeModal, setResetCodeModal] = useState(null);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [showCode, setShowCode] = useState(false);
  
  // Wilayah management
  const [wilayahModal, setWilayahModal] = useState(null);
  const [selectedWilayah, setSelectedWilayah] = useState([]);
  const [isSavingWilayah, setIsSavingWilayah] = useState(false);

  // Login code management (per-session 15min code)
  const [loginCodeModal, setLoginCodeModal] = useState(null);
  const [loginCodeData, setLoginCodeData] = useState(null); // { login_code, expires_at, has_code }
  const [isGeneratingLoginCode, setIsGeneratingLoginCode] = useState(false);
  const [showLoginCode, setShowLoginCode] = useState(false);
  const [activeSessionInfo, setActiveSessionInfo] = useState(null);
  const [isForceLoggingOut, setIsForceLoggingOut] = useState(false);
  const [loginCodeNow, setLoginCodeNow] = useState(Date.now());

  useEffect(() => {
    if (!loginCodeModal) return;
    const t = setInterval(() => setLoginCodeNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [loginCodeModal]);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (roleFilter) params.append("role", roleFilter);
      const queryString = params.toString();
      const res = await axios.get(`${API}/admin/users${queryString ? `?${queryString}` : ""}`, { headers: authHeader() });
      setUsers(res.data);
    } catch (err) {
      // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data user (unauthorized).");
        return;
      }
      toast.error("Gagal memuat data user");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, roleFilter, navigate]);

  const fetchCustomers = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/admin/customers-list`, { headers: authHeader() });
      setCustomers(res.data.customers || []);
    } catch (err) {
      // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data pelanggan (unauthorized).");
        return;
      }
      toast.error("Gagal memuat data pelanggan");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    if (viewMode === "users") {
      fetchUsers();
    } else {
      fetchCustomers();
    }
  }, [fetchUsers, fetchCustomers, viewMode, navigate]);

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`, {}, { headers: authHeader() });
      toast.success("User berhasil disetujui");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyetujui user");
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/suspend`, {}, { headers: authHeader() });
      toast.success("User dinonaktifkan");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menonaktifkan user");
    }
  };

  const handleActivate = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/activate`, {}, { headers: authHeader() });
      toast.success("User diaktifkan kembali");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengaktifkan user");
    }
  };

  // Handle topup amount input with formatting
  const handleTopupAmountChange = (e) => {
    const rawValue = parseFormattedNumber(e.target.value);
    setTopupAmountRaw(rawValue);
    setTopupAmount(formatNumberWithDots(rawValue));
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmountRaw);
    if (!topupModal || !topupAmountRaw || amount <= 0) {
      toast.error("Masukkan jumlah top-up yang valid");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/users/${topupModal.id}/topup`, {
        amount: amount,
        note: topupNote || "Top-up saldo"
      }, { headers: authHeader() });
      toast.success(res.data.message);
      setTopupModal(null);
      setTopupAmount("");
      setTopupAmountRaw("");
      setTopupNote("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal top-up saldo");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal || !deletePassword.trim()) {
      toast.error("Masukkan password admin");
      return;
    }
    setIsDeleting(true);
    try {
      if (deleteModal.isCustomer) {
        // Delete customer
        await axios.post(`${API}/admin/customers/${deleteModal.customer_id}/delete`, {
          admin_password: deletePassword
        }, { headers: authHeader() });
        toast.success(`Pelanggan ${deleteModal.name} berhasil dihapus`);
        fetchCustomers();
      } else {
        // Delete user
        await axios.post(`${API}/admin/users/${deleteModal.id}/delete`, {
          admin_password: deletePassword
        }, { headers: authHeader() });
        toast.success(`User ${deleteModal.username} berhasil dihapus`);
        fetchUsers();
      }
      setDeleteModal(null);
      setDeletePassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCustomerTopup = async () => {
    const amount = parseFloat(topupAmountRaw);
    if (!topupModal || !topupAmountRaw || amount <= 0) {
      toast.error("Masukkan jumlah top-up yang valid");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/customers/${topupModal.customer_id}/topup`, {
        amount: amount,
        note: topupNote || "Top-up saldo"
      }, { headers: authHeader() });
      toast.success(res.data.message || "Top-up berhasil");
      setTopupModal(null);
      setTopupAmount("");
      setTopupAmountRaw("");
      setTopupNote("");
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal top-up saldo");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle generate reset code
  const handleGenerateResetCode = async (user) => {
    setIsGeneratingCode(true);
    try {
      const res = await axios.post(`${API}/admin/users/${user.id}/generate-reset-code`, {}, { headers: authHeader() });
      setGeneratedCode({
        ...res.data,
        user_id: user.id
      });
      setShowCode(true);
      toast.success("Kode reset berhasil dibuat!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat kode reset");
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Fetch existing reset code for user
  const fetchExistingResetCode = async (user) => {
    try {
      const res = await axios.get(`${API}/admin/users/${user.id}/reset-code`, { headers: authHeader() });
      if (res.data.has_code) {
        setGeneratedCode({
          ...res.data,
          user_id: user.id
        });
        setShowCode(false);
      } else {
        setGeneratedCode(null);
      }
      setResetCodeModal(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengambil data");
    }
  };

  // Copy code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Kode disalin ke clipboard!");
  };

  // Open wilayah modal
  const openWilayahModal = async (user) => {
    try {
      const res = await axios.get(`${API}/admin/users/${user.id}/wilayah`, { headers: authHeader() });
      setSelectedWilayah(res.data.allowed_wilayah || []);
      setWilayahModal(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengambil data wilayah");
    }
  };

  // Toggle wilayah selection
  const toggleWilayah = (code) => {
    setSelectedWilayah(prev => 
      prev.includes(code) 
        ? prev.filter(w => w !== code)
        : [...prev, code].sort()
    );
  };

  // Select all wilayah
  const selectAllWilayah = () => {
    setSelectedWilayah([...WILAYAH_CODES]);
  };

  // Clear all wilayah
  const clearAllWilayah = () => {
    setSelectedWilayah([]);
  };

  // Save wilayah
  const handleSaveWilayah = async () => {
    if (!wilayahModal) return;
    setIsSavingWilayah(true);
    try {
      await axios.put(
        `${API}/admin/users/${wilayahModal.id}/wilayah`,
        { wilayah: selectedWilayah },
        { headers: authHeader() }
      );
      toast.success(`Wilayah untuk ${wilayahModal.username} berhasil disimpan`);
      setWilayahModal(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan wilayah");
    } finally {
      setIsSavingWilayah(false);
    }
  };

  // ============ LOGIN CODE (15-min, per session) ============
  const openLoginCodeModal = async (user) => {
    setLoginCodeModal(user);
    setLoginCodeData(null);
    setShowLoginCode(false);
    setActiveSessionInfo(null);
    try {
      const [codeRes, sessRes] = await Promise.all([
        axios.get(`${API}/admin/users/${user.id}/login-code`, { headers: authHeader() }),
        axios.get(`${API}/admin/users/${user.id}/active-session`, { headers: authHeader() })
      ]);
      if (codeRes.data?.has_code) setLoginCodeData(codeRes.data);
      setActiveSessionInfo(sessRes.data || { is_active: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengambil data");
    }
  };

  const handleGenerateLoginCode = async (user) => {
    setIsGeneratingLoginCode(true);
    try {
      const res = await axios.post(`${API}/admin/users/${user.id}/generate-login-code`, {}, { headers: authHeader() });
      setLoginCodeData({ has_code: true, ...res.data });
      setShowLoginCode(true);
      toast.success("Kode login berhasil dibuat (berlaku 15 menit)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat kode login");
    } finally {
      setIsGeneratingLoginCode(false);
    }
  };

  const handleRevokeLoginCode = async (user) => {
    try {
      await axios.delete(`${API}/admin/users/${user.id}/login-code`, { headers: authHeader() });
      setLoginCodeData(null);
      setShowLoginCode(false);
      toast.success("Kode login dihapus");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus kode");
    }
  };

  const handleForceLogout = async (user) => {
    if (!window.confirm(`Reset sesi login ${user.username}? Petugas akan dipaksa logout dari device aktif.`)) return;
    setIsForceLoggingOut(true);
    try {
      const res = await axios.post(`${API}/admin/users/${user.id}/force-logout`, {}, { headers: authHeader() });
      toast.success(res.data.message || "Sesi berhasil di-reset");
      // Refresh active session info
      const sessRes = await axios.get(`${API}/admin/users/${user.id}/active-session`, { headers: authHeader() });
      setActiveSessionInfo(sessRes.data || { is_active: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal reset sesi");
    } finally {
      setIsForceLoggingOut(false);
    }
  };

  // ============ USER SPESIAL (bypass kode login) ============
  const handleToggleSpecial = async (user) => {
    const next = !user.is_special;
    const confirmMsg = next
      ? `Tandai ${user.username} sebagai User Spesial?\n\nUser Spesial bisa login langsung tanpa perlu kode dari admin.`
      : `Cabut status User Spesial dari ${user.username}?\n\nUser akan kembali butuh kode login dari admin.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.post(
        `${API}/admin/users/${user.id}/toggle-special`,
        { is_special: next },
        { headers: authHeader() }
      );
      toast.success(res.data.message || "Status User Spesial diperbarui");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah status User Spesial");
    }
  };

  const formatRemaining = (expiresAtIso) => {
    if (!expiresAtIso) return "";
    const ms = new Date(expiresAtIso).getTime() - loginCodeNow;
    if (ms <= 0) return "Kadaluarsa";
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}m ${s.toString().padStart(2, "0")}d`;
  };

  const statusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      active: "bg-emerald-100 text-emerald-700",
      suspended: "bg-red-100 text-red-700"
    };
    const labels = { pending: "Menunggu", active: "Aktif", suspended: "Nonaktif" };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] || "bg-slate-100 text-slate-600"}`}>
        {labels[status] || status}
      </span>
    );
  };

  const roleBadge = (role) => {
    if (role === "petugas") return <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium">Petugas</span>;
    if (role === "pengawas") return <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Pengawas</span>;
    if (role === "pelanggan") return <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium">Pelanggan</span>;
    if (role === "kasir") return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Kasir</span>;
    if (role === "petugas_lapangan") return <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-medium">Petugas Lapangan</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Agen</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button>
            </Link>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-600" />
              <h1 className="font-heading font-semibold text-lg text-slate-800">Kelola User</h1>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchUsers}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-3xl">
        {/* View Mode Toggle: Users vs Customers */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={viewMode === "users" ? "default" : "outline"}
            size="sm"
            onClick={() => { setViewMode("users"); setStatusFilter(""); setRoleFilter(""); }}
            className={`rounded-full text-xs ${viewMode === "users" ? "bg-sky-600" : ""}`}
            data-testid="view-users"
          >
            <Users className="w-3.5 h-3.5 mr-1" /> Agen/Petugas
          </Button>
          <Button
            variant={viewMode === "customers" ? "default" : "outline"}
            size="sm"
            onClick={() => { setViewMode("customers"); setStatusFilter(""); setRoleFilter(""); }}
            className={`rounded-full text-xs ${viewMode === "customers" ? "bg-teal-600" : ""}`}
            data-testid="view-customers"
          >
            <Mail className="w-3.5 h-3.5 mr-1" /> Pelanggan
          </Button>
        </div>

        {/* Filter - only for Users view */}
        {viewMode === "users" && (
          <div className="space-y-3 mb-4">
            {/* Role Filter */}
            <div className="flex gap-2 flex-wrap">
              <span className="text-xs text-slate-500 self-center mr-1">Role:</span>
              {[
                { value: "", label: "Semua" },
                { value: "petugas", label: "Petugas" },
                { value: "agen", label: "Agen" },
                { value: "pengawas", label: "Pengawas" },
                { value: "kasir", label: "Kasir" },
                { value: "petugas_lapangan", label: "Petugas Lapangan" }
              ].map((r) => (
                <Button
                  key={r.value}
                  variant={roleFilter === r.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setRoleFilter(r.value)}
                  className={`rounded-full text-xs ${roleFilter === r.value ? (r.value === "petugas" ? "bg-sky-600" : r.value === "agen" ? "bg-emerald-600" : r.value === "pengawas" ? "bg-purple-600" : r.value === "kasir" ? "bg-amber-600" : r.value === "petugas_lapangan" ? "bg-rose-600" : "bg-slate-600") : ""}`}
                  data-testid={`filter-role-${r.value || "all"}`}
                >
                  {r.label}
                </Button>
              ))}
            </div>
            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap">
              <span className="text-xs text-slate-500 self-center mr-1">Status:</span>
              {[
                { value: "", label: "Semua" },
                { value: "pending", label: "Menunggu" },
                { value: "active", label: "Aktif" },
                { value: "suspended", label: "Nonaktif" }
              ].map((f) => (
                <Button
                  key={f.value}
                  variant={statusFilter === f.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(f.value)}
                  className={`rounded-full text-xs ${statusFilter === f.value ? (f.value === "pending" ? "bg-amber-600" : f.value === "active" ? "bg-emerald-600" : f.value === "suspended" ? "bg-red-600" : "bg-slate-600") : ""}`}
                  data-testid={`filter-status-${f.value || "all"}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12 text-slate-500">Memuat...</div>
        ) : viewMode === "users" ? (
          /* Users List */
          users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Belum ada user terdaftar</div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <motion.div key={user.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Card className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-semibold text-slate-800">{user.username}</p>
                            {roleBadge(user.role)}
                            {statusBadge(user.status)}
                            {user.is_special && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium inline-flex items-center gap-1" data-testid={`special-badge-${user.id}`}>
                                <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" /> Spesial
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{user.phone}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Daftar: {new Date(user.created_at).toLocaleDateString("id-ID")}
                          </p>
                          {/* Show wilayah info for petugas */}
                          {user.role === "petugas" && (
                            <div className="mt-1">
                              {user.allowed_wilayah && user.allowed_wilayah.length > 0 ? (
                                <p className="text-xs text-emerald-600">
                                  <MapPin className="w-3 h-3 inline mr-1" />
                                  Wilayah: {user.allowed_wilayah.join(", ")}
                                </p>
                              ) : (
                                <p className="text-xs text-amber-600">
                                  <MapPin className="w-3 h-3 inline mr-1" />
                                  Belum diatur wilayahnya
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Saldo</p>
                          <p className="font-heading font-bold text-sky-600" data-testid={`balance-${user.id}`}>
                            {formatCurrency(user.balance || 0)}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        {user.status === "pending" && (
                          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs gap-1" onClick={() => handleApprove(user.id)} data-testid={`approve-${user.id}`}>
                            <Check className="w-3.5 h-3.5" /> Setujui
                          </Button>
                        )}
                        {user.status === "active" && (
                          <>
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-sky-300 text-sky-700" onClick={() => setTopupModal(user)} data-testid={`topup-${user.id}`}>
                              <Wallet className="w-3.5 h-3.5" /> Top-up Saldo
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-red-300 text-red-600" onClick={() => handleSuspend(user.id)} data-testid={`suspend-${user.id}`}>
                              <UserX className="w-3.5 h-3.5" /> Nonaktifkan
                            </Button>
                          </>
                        )}
                        {user.status === "suspended" && (
                          <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-emerald-300 text-emerald-700" onClick={() => handleActivate(user.id)} data-testid={`activate-${user.id}`}>
                            <UserCheck className="w-3.5 h-3.5" /> Aktifkan
                          </Button>
                        )}
                        {/* Tombol Kode Reset Password */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="rounded-full text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" 
                          onClick={() => fetchExistingResetCode(user)} 
                          data-testid={`reset-code-${user.id}`}
                        >
                          <Key className="w-3.5 h-3.5" /> Kode Reset
                        </Button>
                        {/* Tombol Atur Wilayah - untuk petugas & kasir */}
                        {(user.role === "petugas" || user.role === "kasir") && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="rounded-full text-xs gap-1 border-teal-400 text-teal-700 hover:bg-teal-50" 
                            onClick={() => openWilayahModal(user)} 
                            data-testid={`wilayah-${user.id}`}
                          >
                            <MapPin className="w-3.5 h-3.5" /> Atur Wilayah
                          </Button>
                        )}
                        {/* Tombol Kode Login - khusus petugas/agen/kasir/petugas_lapangan */}
                        {(user.role === "petugas" || user.role === "agen" || user.role === "kasir" || user.role === "petugas_lapangan") && user.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full text-xs gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => openLoginCodeModal(user)}
                            data-testid={`login-code-${user.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5" /> Kode Login
                          </Button>
                        )}
                        {/* Tombol User Spesial - toggle bypass kode login */}
                        {(user.role === "petugas" || user.role === "agen" || user.role === "kasir" || user.role === "petugas_lapangan") && user.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className={`rounded-full text-xs gap-1 ${user.is_special ? "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                            onClick={() => handleToggleSpecial(user)}
                            data-testid={`toggle-special-${user.id}`}
                            title={user.is_special ? "Klik untuk mencabut status User Spesial (kembali butuh kode admin)" : "Tandai sebagai User Spesial - bisa login tanpa kode admin"}
                          >
                            <Star className={`w-3.5 h-3.5 ${user.is_special ? "fill-emerald-500 text-emerald-500" : ""}`} />
                            {user.is_special ? "User Spesial ✓" : "User Spesial"}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-red-400 text-red-600 hover:bg-red-50" onClick={() => setDeleteModal(user)} data-testid={`delete-${user.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Hapus
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )
        ) : (
          /* Customers List */
          customers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Belum ada pelanggan terdaftar</div>
          ) : (
            <div className="space-y-3">
              {customers.map((customer) => (
                <motion.div key={customer.customer_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Card className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-slate-800">{customer.name}</p>
                            {roleBadge("pelanggan")}
                            {statusBadge(customer.status || "active")}
                          </div>
                          <p className="text-sm text-slate-500">{customer.email}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Daftar: {customer.created_at ? new Date(customer.created_at).toLocaleDateString("id-ID") : "-"}
                          </p>
                          {customer.last_login && (
                            <p className="text-xs text-slate-400">
                              Login terakhir: {new Date(customer.last_login).toLocaleDateString("id-ID")}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">Saldo</p>
                          <p className="font-heading font-bold text-teal-600" data-testid={`balance-${customer.customer_id}`}>
                            {formatCurrency(customer.balance || 0)}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-teal-300 text-teal-700" onClick={() => setTopupModal({ ...customer, isCustomer: true })} data-testid={`topup-customer-${customer.customer_id}`}>
                          <Wallet className="w-3.5 h-3.5" /> Top-up Saldo
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs gap-1 border-red-400 text-red-600 hover:bg-red-50" onClick={() => setDeleteModal({ ...customer, isCustomer: true })} data-testid={`delete-customer-${customer.customer_id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Hapus
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )
        )}
      </main>

      {/* Top-up Modal */}
      {topupModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setTopupModal(null); setTopupAmount(""); setTopupAmountRaw(""); setTopupNote(""); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-slate-800 mb-1">Top-up Saldo</h3>
            <p className="text-sm text-slate-500 mb-4">
              {topupModal.isCustomer 
                ? `${topupModal.name} (pelanggan) - Saldo: ${formatCurrency(topupModal.balance || 0)}`
                : `${topupModal.username} (${topupModal.role}) - Saldo: ${formatCurrency(topupModal.balance || 0)}`
              }
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Jumlah Top-up (Rp)</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="Contoh: 100.000"
                  value={topupAmount}
                  onChange={handleTopupAmountChange}
                  className="h-12 rounded-xl"
                  data-testid="topup-amount-input"
                />
                {topupAmountRaw && (
                  <p className="text-xs text-slate-500 mt-1">
                    Nominal: {formatCurrency(parseFloat(topupAmountRaw))}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Catatan (opsional)</label>
                <Input
                  type="text"
                  placeholder="Contoh: Transfer BRI"
                  value={topupNote}
                  onChange={(e) => setTopupNote(e.target.value)}
                  className="h-10 rounded-xl"
                  data-testid="topup-note-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setTopupModal(null); setTopupAmount(""); setTopupAmountRaw(""); setTopupNote(""); }}>Batal</Button>
              <Button
                className={`flex-1 rounded-full ${topupModal.isCustomer ? "bg-teal-600 hover:bg-teal-700" : "bg-sky-600 hover:bg-sky-700"}`}
                onClick={topupModal.isCustomer ? handleCustomerTopup : handleTopup}
                disabled={isSubmitting}
                data-testid="topup-submit-btn"
              >
                {isSubmitting ? <div className="spinner w-5 h-5" /> : "Top-up"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setDeleteModal(null); setDeletePassword(""); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-800">
                  Hapus {deleteModal.isCustomer ? "Pelanggan" : "User"}
                </h3>
                <p className="text-sm text-red-600">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-slate-700">
                {deleteModal.isCustomer 
                  ? <>Pelanggan <span className="font-bold">{deleteModal.name}</span> ({deleteModal.email}) akan dihapus beserta semua data top-up dan riwayat transaksi.</>
                  : <>User <span className="font-bold">{deleteModal.username}</span> ({deleteModal.role}) akan dihapus beserta semua data transaksi dan riwayat saldo.</>
                }
              </p>
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                <Lock className="w-3.5 h-3.5 inline mr-1" />
                Masukkan Password Admin
              </label>
              <Input
                type="password"
                placeholder="Password admin Anda"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="h-12 rounded-xl"
                onKeyDown={(e) => e.key === "Enter" && handleDelete()}
                autoFocus
                data-testid="delete-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setDeleteModal(null); setDeletePassword(""); }}>Batal</Button>
              <Button
                className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDelete}
                disabled={isDeleting}
                data-testid="delete-confirm-btn"
              >
                {isDeleting ? <div className="spinner w-5 h-5" /> : `Hapus ${deleteModal.isCustomer ? "Pelanggan" : "User"}`}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reset Code Modal */}
      {resetCodeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setResetCodeModal(null); setGeneratedCode(null); setShowCode(false); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Key className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-800">
                  Kode Reset Password
                </h3>
                <p className="text-sm text-slate-500">{resetCodeModal.username} ({resetCodeModal.role})</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-slate-700">
                Berikan kode ini ke <span className="font-bold">{resetCodeModal.username}</span> untuk mengganti password. Kode berlaku selama 24 jam.
              </p>
            </div>

            {generatedCode?.has_code || generatedCode?.reset_code ? (
              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Kode Reset Aktif</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-xl p-3 font-mono text-lg text-center font-bold tracking-widest">
                    {showCode ? generatedCode.reset_code : "••••••••"}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-12 w-12 rounded-xl"
                    onClick={() => setShowCode(!showCode)}
                    data-testid="toggle-show-code"
                  >
                    {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-12 w-12 rounded-xl"
                    onClick={() => copyToClipboard(generatedCode.reset_code)}
                    data-testid="copy-code-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                {generatedCode.expires_at && (
                  <p className="text-xs text-slate-500 mt-2">
                    Kadaluarsa: {new Date(generatedCode.expires_at).toLocaleString("id-ID")}
                  </p>
                )}
              </div>
            ) : (
              <div className="mb-4 text-center py-4">
                <p className="text-sm text-slate-500 mb-3">Belum ada kode reset aktif untuk user ini.</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setResetCodeModal(null); setGeneratedCode(null); setShowCode(false); }}>
                Tutup
              </Button>
              <Button
                className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white gap-1"
                onClick={() => handleGenerateResetCode(resetCodeModal)}
                disabled={isGeneratingCode}
                data-testid="generate-reset-code-btn"
              >
                {isGeneratingCode ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <>
                    <Key className="w-4 h-4" />
                    {generatedCode ? "Buat Kode Baru" : "Buat Kode"}
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Wilayah Management Modal */}
      {wilayahModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setWilayahModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-800">
                  Atur Wilayah Akses
                </h3>
                <p className="text-sm text-slate-500">{wilayahModal.username} (Petugas)</p>
              </div>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-slate-700">
                Pilih wilayah yang bisa diakses oleh petugas ini. Jika tidak ada yang dipilih, petugas <strong>tidak bisa mengakses tagihan pelanggan manapun</strong>.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 mb-3">
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs rounded-full"
                onClick={selectAllWilayah}
              >
                Pilih Semua
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-xs rounded-full"
                onClick={clearAllWilayah}
              >
                Hapus Semua
              </Button>
            </div>

            {/* Wilayah Selection Grid */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              {WILAYAH_CODES.map((code) => (
                <button
                  key={code}
                  onClick={() => toggleWilayah(code)}
                  className={`p-3 rounded-xl text-sm font-bold transition-all ${
                    selectedWilayah.includes(code)
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`wilayah-code-${code}`}
                >
                  {code}
                </button>
              ))}
            </div>

            {/* Selected Summary */}
            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              <p className="text-xs text-slate-500 mb-1">Wilayah Terpilih ({selectedWilayah.length}):</p>
              <p className="text-sm font-medium text-slate-700">
                {selectedWilayah.length > 0 ? selectedWilayah.join(", ") : "-"}
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setWilayahModal(null)}>
                Batal
              </Button>
              <Button
                className="flex-1 rounded-full bg-teal-600 hover:bg-teal-700 text-white gap-1"
                onClick={handleSaveWilayah}
                disabled={isSavingWilayah}
                data-testid="save-wilayah-btn"
              >
                {isSavingWilayah ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Simpan
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Login Code Modal (per-session, 15 min) */}
      {loginCodeModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => { setLoginCodeModal(null); setLoginCodeData(null); setShowLoginCode(false); setActiveSessionInfo(null); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-800">Kode Login</h3>
                <p className="text-sm text-slate-500">{loginCodeModal.username} ({loginCodeModal.role})</p>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-4">
              <p className="text-sm text-slate-700">
                Kode berlaku <strong>15 menit</strong> dan hanya dapat dipakai <strong>sekali</strong>. Berikan kode ke{" "}
                <span className="font-bold">{loginCodeModal.username}</span> untuk login.
              </p>
            </div>

            {/* Active session indicator */}
            <div className="mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className={`w-4 h-4 ${activeSessionInfo?.is_active ? "text-emerald-600" : "text-slate-400"}`} />
                  <p className="text-sm font-medium text-slate-700">
                    {activeSessionInfo?.is_active ? "Sedang login di 1 device" : "Tidak sedang login"}
                  </p>
                </div>
                {activeSessionInfo?.is_active && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1 border-rose-300 text-rose-600 hover:bg-rose-50 rounded-full"
                    onClick={() => handleForceLogout(loginCodeModal)}
                    disabled={isForceLoggingOut}
                    data-testid="force-logout-btn"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Reset Sesi
                  </Button>
                )}
              </div>
              {activeSessionInfo?.is_active && activeSessionInfo?.login_at && (
                <p className="text-xs text-slate-500 mt-1.5">
                  Login sejak: {new Date(activeSessionInfo.login_at).toLocaleString("id-ID")}
                </p>
              )}
            </div>

            {loginCodeData?.login_code ? (
              <div className="mb-4">
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Kode Aktif</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-100 rounded-xl p-3 font-mono text-2xl text-center font-bold tracking-widest">
                    {showLoginCode ? loginCodeData.login_code : "••••••"}
                  </div>
                  <Button variant="outline" size="sm" className="h-12 w-12 rounded-xl" onClick={() => setShowLoginCode(!showLoginCode)} data-testid="toggle-show-login-code">
                    {showLoginCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" className="h-12 w-12 rounded-xl" onClick={() => copyToClipboard(loginCodeData.login_code)} data-testid="copy-login-code-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-slate-500">
                    Sisa waktu: <span className="font-semibold text-indigo-600">{formatRemaining(loginCodeData.expires_at)}</span>
                  </p>
                  <button
                    type="button"
                    className="text-xs text-rose-600 hover:underline"
                    onClick={() => handleRevokeLoginCode(loginCodeModal)}
                    data-testid="revoke-login-code-btn"
                  >
                    Hapus kode
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-4 text-center py-4">
                <p className="text-sm text-slate-500">Belum ada kode login aktif untuk user ini.</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setLoginCodeModal(null); setLoginCodeData(null); setShowLoginCode(false); setActiveSessionInfo(null); }}>
                Tutup
              </Button>
              <Button
                className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                onClick={() => handleGenerateLoginCode(loginCodeModal)}
                disabled={isGeneratingLoginCode}
                data-testid="generate-login-code-btn"
              >
                {isGeneratingLoginCode ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    {loginCodeData?.login_code ? "Buat Kode Baru" : "Buat Kode"}
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
